import type { DemoScenarioResolved } from 'tio-data/demo/types';
import {
  isFeatureEnabled,
  type DemoSessionSelection,
} from 'tio-employee/utils/career-pathways-demo-data';
import {
  assertNever,
  type DemoFixtureIdentity,
  type DemoProductConfig,
  type DemoScenario,
} from 'tio-employee/demo/types';
import {
  buildDemoIdentityDoc,
  buildDemoIdentityRawDoc,
  buildDemoCompanyDoc,
  buildDemoEmployeeCatchAllDoc,
  buildDemoEmployeeDetailDoc,
  buildDemoPersonDoc,
  resolveDemoFixtureIdentity,
  toRawDoc,
  USER_ID as DEMO_USER_ID,
  PERSON_ID as DEMO_PERSON_ID,
  EMPLOYEE_ID as DEMO_EMPLOYEE_ID,
} from 'tio-employee/demo/identity-fixtures';
import { buildAdminTableMatchers, buildReportDetailRawDoc } from 'tio-employee/demo/admin-fixtures';
import { buildLoanDocumentFetchMatchers } from 'tio-employee/demo/loan-documents-fixtures';
import { buildPslfDemo } from 'tio-employee/demo/products/pslf';
import { buildTasDemo } from 'tio-employee/demo/products/tas';
import { buildContributionsDemo } from 'tio-employee/demo/products/contributions';
import { buildCareerPathwaysDemo } from 'tio-employee/demo/products/career-pathways';
import { buildWellnessDemo } from 'tio-employee/demo/products/wellness';

/**
 * Dispatch a product config to its builder. The exhaustive switch + assertNever
 * guarantees every product kind has a builder at compile time — adding a kind
 * to the union without a case is a type error.
 */
function runBuilder(
  config: DemoProductConfig,
  scenario: DemoScenario,
  identity: DemoFixtureIdentity
): DemoScenarioResolved {
  switch (config.kind) {
    case 'pslf':
      return buildPslfDemo(config, scenario, identity);
    case 'tas':
      return buildTasDemo(config, scenario, identity);
    case 'contributions':
      return buildContributionsDemo(config, scenario, identity);
    case 'career-pathways':
      return buildCareerPathwaysDemo(config, scenario, identity);
    case 'wellness':
      return buildWellnessDemo(config, scenario, identity);
    default:
      return assertNever(config);
  }
}

/**
 * Map the active demo selection onto the concrete product configs whose
 * fixtures should be served. Product availability is derived from the current
 * demo scenario's feature flags (e.g. a scenario with `pslf` enabled serves the
 * PSLF dashboard fixtures). Returns null when no demo products are active so
 * the handler stays a transparent pass-through.
 */
function scenarioFromSelection(selection: DemoSessionSelection): DemoScenario | null {
  const products: DemoProductConfig[] = [];

  if (isFeatureEnabled('pslf')) {
    products.push({ kind: 'pslf', scenarioId: 'full-journey' });
  }

  if (isFeatureEnabled('tuitionAssistance')) {
    products.push({
      kind: 'tas',
      scenarioId: 'in-progress',
      taPaymentModelId: selection.taPaymentModelId ?? 'reimbursement',
    });
  }

  if (isFeatureEnabled('studentLoanRepayment')) {
    products.push({ kind: 'contributions', scenarioId: 'in-progress' });
  }

  if (products.length === 0) {
    return null;
  }

  return {
    persona: selection.userScenarioId ?? 'default',
    client: selection.clientId,
    role: selection.roleId,
    products,
  };
}

// Match the session user's own findRecord BUILDER URL (`…/users/demo-user`,
// optionally with an `?include=` query) — never a user SUBROUTE like
// `…/users/demo-user/user-profile` (wellness) or `…/users/demo-user/summarize-loans`,
// which carry their own demo guards/seams. Built from the shared id so it can
// never drift from the identity doc.
const SESSION_USER_URL_RE = new RegExp(`/users/${DEMO_USER_ID}(?:\\?|$)`);
const matchesSessionUserEndpoint = (url: string) => SESSION_USER_URL_RE.test(url);

// Same shape for the session PERSON findRecord builder (`…/people/demo-person`,
// optionally `?include=…`) — never a subroute. The loans/detail + finance/history
// routes re-load `findRecord('person', sessionPersonId, { include })` via the
// builder; sessionPersonId is always the demo person id.
const SESSION_PERSON_URL_RE = new RegExp(`/people/${DEMO_PERSON_ID}(?:\\?|$)`);
const matchesSessionPersonEndpoint = (url: string) => SESSION_PERSON_URL_RE.test(url);

// The session EMPLOYEE's own findRecord builder URL (`…/employees/<id>`,
// optionally `?include=…`) — never a subroute (accounting endpoints etc. have
// their own matchers). The shipped PSLF route reads it with `reload: true`,
// which always reaches the fetch seam.
const SESSION_EMPLOYEE_URL_RE = new RegExp(`/employees/${DEMO_EMPLOYEE_ID}(?:\\?|$)`);
const matchesSessionEmployeeEndpoint = (url: string) => SESSION_EMPLOYEE_URL_RE.test(url);

// Company findRecord builder reads (`…/companies/<id>`, never a subroute).
// The fetch-seam sibling of the op-path company catch-all: the shipped PSLF
// route follows the employee read with `findRecord('company', id,
// { include: 'pslf-groups', reload: true })`, which lands here.
const COMPANY_URL_RE = /\/companies\/[^/?]+(?:\?|$)/;
const matchesCompanyEndpoint = (url: string) => COMPANY_URL_RE.test(url);

/**
 * The session-identity matchers, served for EVERY active demo session
 * independent of which products are enabled (a product-less selection — e.g.
 * wellness/career-pathways — must still get a demo `/me`). Serves the fabricated
 * identity document on every seam it can be requested from:
 *  - the `/me` bootstrap (raw fetch and the `meRequest` builder both reach the
 *    fetch interceptor as `GET /me?include=…`);
 *  - the authenticated route's (and `getEmployeeIdsAsync`'s)
 *    `findRecord('user', id)` BUILDER read — carries no SkipCache, so the demo
 *    request HANDLER defers it and it reaches the fetch interceptor as
 *    `GET /users/<id>`. Without this it leaked live (`/users/demo-user` → 404,
 *    blanking the authenticated route's main content);
 *  - legacy `store.findRecord('user', …)` (SkipCache) — served by the
 *    request-manager op-matcher below.
 */
function identityResolved(selection: Required<DemoSessionSelection>): DemoScenarioResolved {
  return {
    // findRecord('user') → store.push (already-normalized, camelCase keys).
    matchers: [
      {
        matcher: { kind: 'op', op: 'findRecord', type: 'user' },
        docs: [buildDemoIdentityDoc(selection)],
      },
    ],
    seed: [],
    fetchMatchers: [
      // /me bootstrap → session-context store.request(meRequest) / serializer
      // (raw-API, dasherized keys).
      { urlIncludes: '/me?include=', method: 'GET', json: buildDemoIdentityRawDoc(selection) },
      // The route's findRecord('user', id) builder read (same raw doc — it flows
      // through the normal response/cache pipeline, so content.data materializes
      // as the demo UserModel exactly like /me).
      {
        urlMatches: matchesSessionUserEndpoint,
        method: 'GET',
        json: buildDemoIdentityRawDoc(selection),
      },
      // The session employee's builder findRecord (PSLF route, reload: true).
      {
        urlMatches: matchesSessionEmployeeEndpoint,
        method: 'GET',
        json: toRawDoc(buildDemoEmployeeDetailDoc(selection)),
      },
      // Company builder findRecord — id ECHOED, mirroring the op-path
      // company catch-all: any company id referenced by the fixture graph
      // resolves to the demo company instead of blocking to `data: null`
      // (the shipped PSLF route reads `company.pslfGroups` off the result).
      {
        urlMatches: matchesCompanyEndpoint,
        method: 'GET',
        json: (url: string) => {
          const doc = toRawDoc(buildDemoCompanyDoc(selection));
          const id = COMPANY_URL_RE.exec(url)?.[0]?.split('/')[2]?.split('?')[0];
          if (id && doc.data && !Array.isArray(doc.data)) {
            doc.data.id = id;
          }
          return doc;
        },
      },
      // Learning Partner Network listing (the /lpn route). The route reads
      // `content.meta.page.total`; an inert block body has no `page` and
      // crashed the route ("reading 'total'"). A demo company has no LPN
      // catalog wired yet — an empty, well-formed page renders the clean
      // empty state.
      {
        urlMatches: (url: string) => /\/lpn-pages(\?|$)/.test(url),
        method: 'GET',
        json: { data: [], meta: { page: { total: 0 } } },
      },
      // Account-activity loan documents (table + inline-PDF preview), gated on
      // the debt axis — [] when the session has no linked loans, so the
      // network-free block serves the empty documents state.
      ...buildLoanDocumentFetchMatchers(selection),
    ],
  };
}

// A complete, wire-faithful EMPTY OpenSearch response (matches the
// `OpenSearchResponse` shape in tio-data/models/search: hits.total {value,
// relation}, hits.maxScore, took, timedOut) so every consumer — including any
// that reads those fields without defaulting — gets a well-formed zero-result
// payload. A safe inert result for any demo search not otherwise scripted.
const EMPTY_SEARCH_RESPONSE = {
  took: 0,
  timedOut: false,
  hits: { total: { value: 0, relation: 'eq' }, maxScore: 0, hits: [] },
  aggregations: {},
};

// Match ONLY the OpenSearch search endpoint (`…/api/v2/search`, optionally with a
// query string) — never `…/api/v2/search.csv` (downloadCsv), `…/api/v2/search/…`
// subroutes (mapping/fields/explain/stats), or `…/api/v2/searches`.
const matchesSearchEndpoint = (url: string) => /\/api\/v2\/search(?:\?|$)/.test(url);

/**
 * Compile the active demo selection into the matchers + seed the tio-data demo
 * handler consumes. ALWAYS returns the identity matchers (so the whole session
 * is demo-coherent), plus any active product's matchers.
 */
export function resolveDemoScenario(
  selection: Required<DemoSessionSelection>
): DemoScenarioResolved {
  const identity = identityResolved(selection);
  const scenario = scenarioFromSelection(selection);

  const matchers: DemoScenarioResolved['matchers'] = [...identity.matchers];
  const seed: NonNullable<DemoScenarioResolved['seed']> = [...(identity.seed ?? [])];
  const fetchMatchers: NonNullable<DemoScenarioResolved['fetchMatchers']> = [
    ...(identity.fetchMatchers ?? []),
  ];

  if (scenario) {
    const fixtureIdentity = resolveDemoFixtureIdentity(selection);
    for (const product of scenario.products) {
      const part = runBuilder(product, scenario, fixtureIdentity);
      matchers.push(...part.matchers);
      if (part.seed) {
        seed.push(...part.seed);
      }
      if (part.fetchMatchers) {
        fetchMatchers.push(...part.fetchMatchers);
      }
    }
  }

  // Defense-in-depth: a catch-all `company` findRecord matcher, appended LAST so
  // any product-specific company fixture (e.g. PSLF's) still wins via the
  // handler's first-match-wins lookup. The handler echoes the requested id, so a
  // company lookup that NO product scripted — including ids reachable only via a
  // sideloaded relationship like `demo-slr-company-1` — resolves to the demo
  // company instead of falling through to the real API and 404-ing. Without
  // this, an active demo on contributions/TAS (which sideload but never register
  // a company findRecord) breaks with "demo-slr-company-1 could not be found".
  matchers.push({
    matcher: { kind: 'op', op: 'findRecord', type: 'company' },
    docs: [buildDemoCompanyDoc(selection)],
  });

  // Defense-in-depth: a catch-all OpenSearch matcher, appended LAST so any
  // product-specific search (e.g. an approval-queue TasProgramInstance fixture)
  // still wins via first-match-wins. The classic admin reporting + TAS approval
  // screens POST to /api/v2/search with the DEMO company id; with no matcher that
  // hits the real API, which 422s ("User does not belong to company id provided")
  // because the real session user is not a member of the demo company. Returning
  // an empty result keeps every demo search inert (a clean empty state) instead
  // of leaking to the live backend.
  matchers.push({
    matcher: { kind: 'builder', method: 'POST', urlMatches: matchesSearchEndpoint },
    content: EMPTY_SEARCH_RESPONSE,
  });

  // WarpDrive wave 1.5: the reporting metadata GETs (mapping / fields /
  // key-label-maps) used to reach the API via the search adapter's raw `ajax`
  // (intercepted by the `fetchMatchers` below). The reporting call sites now
  // issue them through `store.request(builder)` (SkipCache GET builders), so
  // they hit the demo HANDLER instead of the fetch seam — add builder-kind
  // matchers mirroring those fetch fixtures so chart/report builders keep
  // falling back to their static client-side field catalogs in demo
  // (mapping: null / fields: { fields: [] } / key-label-maps: {}). Both seams
  // are kept: admin (wave 3) still routes these through the raw adapter.
  matchers.push(
    {
      matcher: { kind: 'builder', method: 'GET', urlIncludes: '/api/v2/search/mapping' },
      content: null,
    },
    {
      matcher: { kind: 'builder', method: 'GET', urlIncludes: '/api/v2/search/fields' },
      content: { fields: [] },
    },
    {
      matcher: { kind: 'builder', method: 'GET', urlIncludes: '/api/v2/search/key-label-maps' },
      content: {},
    },
    {
      matcher: {
        kind: 'builder',
        method: 'GET',
        urlIncludes: '/api/v2/search/employee-custom-field-mappings',
      },
      content: [],
    }
  );

  // Catch-all `person` findRecord — same defense-in-depth as the company
  // catch-all above (echoed id; product person fixtures win first). Without it,
  // surfaces that look up the session person (e.g. the loans/account pages in a
  // product-less demo) fall through to the real API as /people/<demo-id> → 404.
  matchers.push({
    matcher: { kind: 'op', op: 'findRecord', type: 'person' },
    docs: [buildDemoPersonDoc(selection)],
  });

  // Builder-seam twin of the person catch-all: the loans/detail + finance/history
  // routes re-load the SESSION person via the `findRecord('person', id)` BUILDER
  // (no SkipCache → defers past the request handler to the fetch seam as
  // `GET /people/demo-person`). Serve the same person doc (raw/dasherized) so it
  // materializes through the normal pipeline instead of leaking live.
  fetchMatchers.push({
    urlMatches: matchesSessionPersonEndpoint,
    method: 'GET',
    json: toRawDoc(buildDemoPersonDoc(selection)),
  });

  // Catch-all `employee` findRecord — same pattern (echoed id; product
  // employee fixtures win first). Admin drill-ins reach arbitrary employee
  // ids from the directory/queue in products that registered no employee
  // findRecord of their own.
  matchers.push({
    matcher: { kind: 'op', op: 'findRecord', type: 'employee' },
    docs: [buildDemoEmployeeCatchAllDoc(selection)],
  });

  // The cross-product ADMIN tables (employee directory, eligibility files,
  // TAS payments, saved-reports listing) get authored demo collections —
  // these surfaces exist for the admin role in every product, so they're
  // registered here rather than in a product builder. After product matchers
  // (first-match-wins), before the empty catch-alls below.
  matchers.push(...buildAdminTableMatchers(resolveDemoFixtureIdentity(selection)));

  // Cross-product empty-collection catch-alls for everything else unscripted.
  // Appended LAST so product fixtures (e.g. the TAS template/instance queries,
  // the contributions transaction timeline) still win; unscripted reads
  // resolve to a clean empty state — never the live API (whose responses
  // would mix real data into a demo session).
  for (const type of [
    'tas-program-template',
    'accounting-transaction',
    // The admin employee drill-in queries PSLF forms for any employee row;
    // the PSLF product's own pslf-form fixture wins first when PSLF is on.
    'pslf-form',
    // Program-template admin screens load enum option lists (e.g.
    // AcademicPerformance) — empty keeps the dropdowns inert.
    'enum',
  ]) {
    // Pagination meta included: paginated admin tables read
    // `result.meta.page.total` without defaulting.
    matchers.push({
      matcher: { kind: 'op', op: 'query', type },
      docs: [{ data: [], meta: { page: { total: 0, offset: 0, limit: 50 } } }],
    });
  }

  // The SAME /api/v2/search seam is still hit by the `search` adapter's raw
  // `ajax` in the ADMIN app (wave-3 deferred — its call sites have not yet
  // moved to the builder), which bypasses BOTH the request-manager handler AND
  // window.fetch, so it must be served via demoJsonFor. (Employee/tio-reporting
  // POST searches now route through the builder + the demo handler matcher
  // above.) Mirror the empty result here so reporting search screens stay inert
  // in demo, not 422.
  fetchMatchers.push({
    urlMatches: matchesSearchEndpoint,
    method: 'POST',
    json: EMPTY_SEARCH_RESPONSE,
  });

  // Cross-product raw-fetch catch-alls, appended LAST so any product-specific
  // matcher (e.g. the contributions accounting fixtures) still wins via
  // first-match-wins. These endpoints are reached from shared surfaces in
  // EVERY demo session, regardless of which products registered fixtures:
  //
  // - The employee service's `contributionsState` / `futurePayments` tracked
  //   functions call the EmployeeAdapter accounting endpoints (seam B,
  //   demoJsonFor) from product-agnostic components; the demo identity employee
  //   always carries an SLR plan, so they fire even in a TAS-only demo. An
  //   empty payments payload with NO `summary.total` makes consumers
  //   (fetchEmployerContributions) fall back to their local demo snapshot —
  //   empty-safe, never live.
  // - The custom telemetry service (tio-common/services/telemetry) flushes
  //   event batches and rrweb session-replay chunks to the API's public
  //   telemetry endpoints via plain `fetch`. A demo session must not emit its
  //   fabricated identity/interactions to the live API, so both are swallowed
  //   with an inert 200 while demo is active.
  fetchMatchers.push(
    { urlIncludes: '/accounting_payments', method: 'GET', json: { payments: [], summary: {} } },
    { urlIncludes: '/accounting_preview', method: 'GET', json: [] },
    { urlIncludes: '/api/public/telemetry', method: 'POST', json: {} },
    { urlIncludes: '/api/public/session-replay', method: 'POST', json: {} },
    // Company register conditions. The post-auth terms gate
    // (sessionContext.hasAcceptedAllCustomRegisterTerms) compares the demo
    // identity's fixture agreements against these; LIVE conditions carry real
    // ids that can never match, which popped the Sign-Up/T&C dialog mid-demo
    // and then 400'd ("demo-user is not a valid value for id") on accept. A
    // demo company has no custom register terms: an empty collection keeps
    // the gate satisfied. (pre-register.load() already zeroes conditions in
    // demo; this covers direct/refresh fetches of the endpoint.)
    {
      urlMatches: (url) => /\/preregister\/[^/]+\/conditions(?:\/)?(?:\?|$)/.test(url),
      method: 'GET',
      json: { data: [] },
    },
    // SLR alerts for the session person (loans/account surfaces fetch this in
    // every product). 'non-slr-never' is the wire value for "this person has
    // never had SLR" — a coherent empty state; the contributions product's
    // richer fixture wins first when SLR is in the demo.
    {
      urlIncludes: '/slr-alerts',
      method: 'GET',
      json: { page_state: 'non-slr-never', alerts: [], contribution_account: null },
    },
    // TAS limits status for cells where no product registered one ({ data: [] }
    // is the wire shape for "no limits configured").
    { urlIncludes: '/api/v2/limits/status', method: 'GET', json: { data: [] } },
    // Reporting metadata endpoints (report-viewer / tas-programs / chart
    // builder), reached via the search adapter's raw `ajax` in the ADMIN app
    // (wave-3 deferred). `mapping: null` + `fields: { fields: [] }` push
    // chart/report builders to their static fallback field catalogs — fully
    // client-side, never live. (Employee/tio-reporting now route these through
    // the builder + the demo handler matchers added above; these fetch fixtures
    // remain for the admin app until wave 3.)
    { urlIncludes: '/api/v2/search/mapping', method: 'GET', json: null },
    { urlIncludes: '/api/v2/search/fields', method: 'GET', json: { fields: [] } },
    { urlIncludes: '/api/v2/search/key-label-maps', method: 'GET', json: {} },
    { urlIncludes: '/api/v2/search/employee-custom-field-mappings', method: 'GET', json: [] },
    // Saved-report reads that reach the raw-fetch seam (non-SkipCache builder
    // GETs). Detail URLs need a SINGLE-resource doc (`data.attributes` reads);
    // the listing shape answers only the bare collection URL.
    {
      urlMatches: (url: string) => /\/api\/v2\/reports\/[^/?]+/.test(url),
      method: 'GET',
      json: buildReportDetailRawDoc(resolveDemoFixtureIdentity(selection)),
    },
    {
      urlMatches: (url: string) => /\/api\/v2\/reports(?:\?|$)/.test(url),
      method: 'GET',
      json: { data: [] },
    }
  );

  return { matchers, seed, fetchMatchers };
}
